/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 *
 * Created by Justin K Kazmierczak.
 */

/*
 * email.header — the compact masthead row on every email.
 *
 * FOUNDER SPEC (2026-07): a SMALL brand lockup on the LEFT — the logo mark WITH the
 * app-name wordmark beside it (F20, 2026-07-24) — and a "View in browser" link on
 * the RIGHT, one quiet row. It is NOT the hero; the email's own primary image (e.g.
 * a title card) is, so the lockup stays small (~32px logo + a compact serif wordmark)
 * rather than the old 60px logo + big wordmark stack that read as a masthead.
 *
 * Two hard rules baked in here:
 *   1. EVERY URL resolves from the PUBLIC email origin (uat/src/email/urls.js →
 *      config.email.publicBaseUrl), NEVER config.my.app.url. The old code used the
 *      env host, so recipients got https://<host>.test/logo.png — a broken image.
 *      No public origin configured ⇒ the <img> is OMITTED (text wordmark instead)
 *      rather than emitting a dev/relative URL. Fail closed.
 *   2. Two columns via <table> + align/width only — no flexbox/grid (Outlook), and
 *      fixed width/height on the <img> so a blocked-image client shows alt text in
 *      a row that keeps its shape.
 *
 * "View in browser" renders ONLY when `docId` is supplied. Marketing docs get it
 * injected by the compiler (app/models/egt.email.compile.js) as the send-time token
 * __EGTVIEWDOC__, which egt.email.blast.buildTrackedTemplate swaps for the real doc
 * id. It references the DOCUMENT only — never a recipient (EML-12). Transactional
 * platform templates pass no docId, so they carry no link at all.
 */
var _ = require("../../../uam/module.js")(module.exports, {
    namespace: "email.header",
    title: "Email Header",
    description: "Compact masthead: small logo left, 'View in browser' right. Absolute public URLs only.",
    supportServer: true,
    control: false,
    foruseIn: ["email"],
    fields: {
        "docId": {
            title: "Email Document Id",
            type: "string",
            description: "The email document this send renders. When present a right-aligned 'View in browser' link is shown, pointing at <publicBaseUrl>/email/view/<docId>. References the document only — never a recipient. Omit on transactional email.",
            required: false,
            default: ""
        }
    }
});

var $ = require("../../../uam/scafolding.js");
var urls = require("../email/urls.js");

// Small — the founder's target is ~2rem (32px) tall. /logo.png is a 512px square,
// so painting it at 32 stays crisp on retina. width/height are also set as HTML
// attributes: Outlook ignores CSS sizing on images, and an image-blocking client
// keeps the row's shape from the attributes alone.
var LOGO_PX = 32;

function escAttr(s) {
    return String(s == null ? "" : s)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

exports.render = async function (options) {
    var { html } = await exports.renderEmail(options);
    return [{
        n: "ua.element.serverNotSupported",
        inner: {
            n: "json",
            type: "ua/interface",
            by: "email.header",
            i: JSON.stringify({
                n: "html",
                html: `<div style="max-width:480px;margin:0 auto;font-family:Arial,Helvetica,sans-serif;">${html}</div>`
            })
        }
    }];
};

exports.renderEmail = async function (options) {
    options = options || {};
    var cfg = $.config || {};
    var app = (cfg.my && cfg.my.app) ? cfg.my.app : {};
    var colors = app.colors || {};
    var fonts = app.fonts || {};
    var appName = app.name ? app.name : "The Universe";
    // Accent for the wordmark: the tenant's accent, else its primary, else a
    // NEUTRAL grey — NEVER gold. EGT sets primary=#C48E2F, so its output is
    // unchanged; a tenant without brand colours gets #6C757D, not EGT gold.
    var accent = colors.accent || colors.primary || "#6C757D";
    // Heading font for the wordmark: config-driven, falls back to the body font,
    // then to a neutral sans — NEVER a hardcoded serif. EGT sets
    // my.app.fonts.heading = "'Playfair Display',Georgia,'Times New Roman',serif",
    // so its wordmark is unchanged; a tenant without fonts gets Arial (no serif).
    var headingFont = fonts.heading || fonts.body || "Arial,Helvetica,sans-serif";

    // The PUBLIC origin — never the env host. "" ⇒ degrade, never emit a dev URL.
    var base = urls.emailBaseUrl(cfg);
    var logoUrl = base ? (base + "/logo.png") : "";

    var docId = (typeof options.docId === "string") ? options.docId.trim() : "";
    var viewUrl = (base && docId) ? (base + "/email/view/" + encodeURIComponent(docId)) : "";

    // LEFT — the brand lockup: the small logo mark AND the app-name wordmark beside
    // it (F20 — "logo top-left WITH the word beside it"). The wordmark reads from
    // config.my.app.name (tenant-safe — "EGT" here, the right name elsewhere) and
    // carries the brand colour + a serif face for cohesion with the serif titles.
    // No public origin ⇒ a quiet wordmark alone, so the row never carries a broken
    // image. With an origin, both logo and wordmark link to the site.
    var wordmark = `<span style="font-family:${headingFont};`
        + `font-size:20px;font-weight:bold;color:${accent};line-height:${LOGO_PX}px;letter-spacing:0.5px;">`
        + `${escAttr(appName)}</span>`;

    var markHtml;
    if (logoUrl) {
        var img = `<img src="${escAttr(logoUrl)}" alt="${escAttr(appName)}" width="${LOGO_PX}" height="${LOGO_PX}" `
            + `style="display:block;border:0;outline:none;text-decoration:none;width:${LOGO_PX}px;height:${LOGO_PX}px;" />`;
        // Nested table (not flexbox) so the logo+wordmark lockup holds in Outlook;
        // two links around inline content, never one anchor wrapping a table.
        markHtml = `<table border="0" cellpadding="0" cellspacing="0" role="presentation" style="border-collapse:collapse;">
          <tr>
            <td valign="middle" width="${LOGO_PX}" style="padding:0;vertical-align:middle;">`
              + `<a href="${escAttr(base)}" style="text-decoration:none;border:0;display:inline-block;">${img}</a>`
            + `</td>
            <td valign="middle" style="padding:0 0 0 8px;vertical-align:middle;">`
              + `<a href="${escAttr(base)}" style="text-decoration:none;border:0;">${wordmark}</a>`
            + `</td>
          </tr>
        </table>`;
    } else {
        markHtml = wordmark;
    }

    // RIGHT — quiet utility link. &nbsp; keeps the cell (and the row height) when
    // there is no doc to view, so the layout never collapses.
    var viewHtml = viewUrl
        ? `<a href="${escAttr(viewUrl)}" style="font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#8C8C8C;text-decoration:underline;">View in browser</a>`
        : "&nbsp;";

    var html = `<tr>
  <td style="padding:20px 20px 8px 20px;">
    <table width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="width:100%;border-collapse:collapse;">
      <tr>
        <td align="left" valign="middle" style="padding:0;vertical-align:middle;">
          ${markHtml}
        </td>
        <td align="right" valign="middle" style="padding:0 0 0 12px;vertical-align:middle;text-align:right;font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#8C8C8C;">
          ${viewHtml}
        </td>
      </tr>
    </table>
  </td>
</tr>`;

    var textParts = [appName];
    if (base) { textParts.push(base); }
    if (viewUrl) { textParts.push("View in browser: " + viewUrl); }
    var text = textParts.join("\n") + "\n";

    return { html, text };
};
